import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { useRecoilValue } from 'recoil'
import { toast } from 'sonner'
import {
    ArrowDownTrayIcon,
    ArrowPathRoundedSquareIcon,
    TicketIcon,
    TrashIcon,
} from '@heroicons/react/24/solid'
import { Spinner, Tooltip } from '@material-tailwind/react'
import { userActivityStatePrc } from '../../../store/userActivityState'
import DataTableShell from '../../../Components/table/DataTableShell'
import TableToolbar from '../../../Components/table/TableToolbar'
import TableSearch from '../../../Components/table/TableSearch'
import {
    TableActionGroup,
    TableEmptyRow,
    TableIdCell,
    TableSkeletonRows,
    TableStatusChip,
} from '../../../Components/table/TableCells'
import useClientTable from '../../../Components/table/useClientTable'
import exportCouponXlsx from '../../../middlewares/Pricing/couponPromotionsExportXls'

const TABLE_HEAD = ['Request ID', 'Coupon Name', 'Request Date', 'Requested by', 'Status', 'Actions']
const matchesCoupon = (item, query) =>
    !query ||
    String(item.couponId ?? '').toLowerCase().includes(query) ||
    String(item.batchId ?? '').toLowerCase().includes(query) ||
    String(item.addedBy ?? '').toLowerCase().includes(query)
const newestFirst = (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)

const CouponsTable = ({ isAltered }) => {
    const [coupReqList, setCoupReqList] = useState([])
    const [coupAcReqList, setCoupAcReqList] = useState([])
    const [isChanged, setIsChanged] = useState(false)
    const [isAltereds, setIsAltereds] = useState()
    const [downloadingBatch, setDownloadingBatch] = useState({})
    const [isLoading, setIsLoading] = useState(true)
    const lastActivity = useRecoilValue(userActivityStatePrc)
    const {
        searchTerm,
        handleSearch,
        currentPage,
        setCurrentPage,
        pageItems,
        totalPages,
        totalFiltered,
    } = useClientTable({
        data: coupReqList,
        pageSize: 10,
        filterFn: matchesCoupon,
        sortFn: newestFirst,
    })

    useEffect(() => {
        const toggleInterval = setInterval(() => {
            setIsAltereds(prevState => !prevState)
        }, 2000) 
        return () => {
            clearInterval(toggleInterval)
        }
    }, [])

    useEffect(() => {
        axios.get('/api/coupon-promotions/all-batches')
            .then(response => {
                if (response.data.message) {
                    console.log("Cannot get batches")
                } else {
                    setCoupReqList(response.data.result)

                    axios.get(`https://api-central.outdoorequippedservice.com/api/v1/coupon-promotions/promotions/all-batch`)
                    .then(res => setCoupAcReqList(res.data))
                }
            })
            .catch(error => {
                console.error('Error:', error)
            })
            .finally(() => setIsLoading(false))
    }, [isAltered, isAltereds, isChanged])

    const handleExportData = async (batchId) => {
        setDownloadingBatch(prevState => ({ ...prevState, [batchId]: true }))
        await exportCouponXlsx(batchId)
        setDownloadingBatch(prevState => ({ ...prevState, [batchId]: false }))
    }

    const handleDeleteReq = (requestId) => {
        const isConfirmed = window.confirm(`Are you sure you want to delete this request: ${requestId}?`)

        if (isConfirmed) {
            axios.delete(`/api/coupon-promotions/delete/${requestId}`)
                .then(response => {
                    if (response.data.message) {
                        toast.success(`${response.data.message}`)
                        setIsChanged(prevState => !prevState)
                        axios.delete(`https://api-central.outdoorequippedservice.com/api/v1/coupon-promotions/promotions/batch/${requestId}`)
                        .then(response => {console.log(response.data.result)})

                    } else {
                        toast.error(`${response.data.message}`)
                        console.log('Error:', response.data.error)
                    }
                })
                .catch(error => {
                    console.error('Error:', error)
                })
        }
    }

    const handleReopenBatch = (batchId) => {

        const updatedStatus = {
            status: {
                name: 'open',
                message: 'OK!'
            }
        }
        axios.put(`/api/coupon-promotions/update/status/${batchId}`, updatedStatus)
            .then(response => {
                if(response.data.message) {
                    setIsChanged(prevState => !prevState)
                    console.log('repoening', batchId)
                    axios.delete(`https://api-central.outdoorequippedservice.com/api/v1/coupon-promotions/promotions/batch/${batchId}`)
                    .then(response => {console.log(response.data.result)})
                }else {
                    toast.error(`${response.data.message}`)
                    console.log('Error:', response.data.error)
                }
            })
    }

    return (
        <DataTableShell
            minWidth="980px"
            toolbar={
                <TableToolbar
                    kicker="Coupon promotions"
                    title="Coupon Requests"
                    meta={`${totalFiltered} of ${coupReqList.length} requests · Last activity ${
                        lastActivity === 'NaN min ago' ? '0 min ago' : lastActivity
                    }`}
                >
                    <TableSearch
                        id="coupon-request-search"
                        value={searchTerm}
                        onChange={handleSearch}
                        placeholder="Search requests"
                    />
                </TableToolbar>
            }
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
        >
            <thead>
                <tr>
                    {TABLE_HEAD.map(head => <th key={head}>{head}</th>)}
                </tr>
            </thead>
            <tbody>
                {isLoading ? (
                    <TableSkeletonRows columns={TABLE_HEAD.length} rows={9} />
                ) : pageItems.length === 0 ? (
                    <TableEmptyRow
                        columns={TABLE_HEAD.length}
                        title="No coupon requests found"
                        description="Try a different request ID, coupon ID, or requester."
                    />
                ) : pageItems.map(({ batchId, couponId, addedBy, updatedAt, status }) => {
                    const date = new Date(updatedAt)
                    const formattedTime = date.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                    })
                    const formattedDate = date.toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                    })
                    const coupon = coupAcReqList.find(item => item.batchId === batchId)
                    const couponTitle = coupon?.promotionDetails?.[0]?.title
                    const statusName = status?.name || 'error'

                    return (
                        <tr key={batchId}>
                            <td><TableIdCell>{batchId}</TableIdCell></td>
                            <td>
                                <div className="flex items-center gap-2">
                                    <Tooltip content={`Coupon ID: ${couponId}`}>
                                        <TicketIcon className="h-5 w-5 shrink-0 text-signal" />
                                    </Tooltip>
                                    {couponTitle || <span className="animate-pulse text-muted">Loading title…</span>}
                                </div>
                            </td>
                            <td>{`${formattedDate} ${formattedTime}`}</td>
                            <td>{addedBy}</td>
                            <td>
                                <Tooltip content={status?.message || statusName}>
                                    <span><TableStatusChip status={statusName} /></span>
                                </Tooltip>
                            </td>
                            <td>
                                <TableActionGroup>
                                    {statusName === 'completed' && (
                                        <Tooltip content="Export file">
                                            <button
                                                type="button"
                                                className="nx-btn-ghost !px-2"
                                                onClick={() => handleExportData(batchId)}
                                                disabled={downloadingBatch[batchId]}
                                                aria-label={`Export coupon request ${batchId}`}
                                            >
                                                {downloadingBatch[batchId]
                                                    ? <Spinner className="h-5 w-5" />
                                                    : <ArrowDownTrayIcon className="h-5 w-5" />}
                                            </button>
                                        </Tooltip>
                                    )}
                                    {statusName !== 'open' && (
                                        <Tooltip content="Resync coupon">
                                            <button
                                                type="button"
                                                className="nx-btn-secondary !px-2"
                                                onClick={() => handleReopenBatch(batchId)}
                                                aria-label={`Resync coupon request ${batchId}`}
                                            >
                                                <ArrowPathRoundedSquareIcon className="h-5 w-5" />
                                            </button>
                                        </Tooltip>
                                    )}
                                    <Tooltip content="Delete request">
                                        <button
                                            type="button"
                                            className="nx-btn-ghost !px-2 text-red-400"
                                            onClick={() => handleDeleteReq(batchId)}
                                            aria-label={`Delete coupon request ${batchId}`}
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </Tooltip>
                                </TableActionGroup>
                            </td>
                        </tr>
                    )
                })}
            </tbody>
        </DataTableShell>
    )
}

export default CouponsTable
